import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Calendar, Users, Clock, Gift, ArrowRight } from "lucide-react";
import api, { apiErr, fmtDate, money } from "@/lib/api";
import { POOL_NIGHT, LOGO, SIGNATURE } from "@/constants/media";
import { Reveal } from "@/components/brand";

export default function InvitationLanding() {
  const { code } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | error | declined | accepted
  const [inv, setInv] = useState(null);
  const [exp, setExp] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get(`/invitations/lookup/${code}`)
      .then(({ data }) => {
        setInv(data.invitation); setExp(data.experience);
        const st = data.invitation.status;
        if (["revoked", "expired", "used", "declined"].includes(st)) { setState("error"); setMsg(st); }
        else setState("ok");
      })
      .catch((e) => { setState("error"); setMsg(apiErr(e)); });
  }, [code]);

  const accept = async () => {
    try {
      await api.post(`/invitations/${code}/accept`);
      const q = new URLSearchParams({ invite: code });
      if (inv.experience_id) q.set("experience", inv.experience_id);
      nav(`/book?${q.toString()}`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const decline = async () => {
    try { await api.post(`/invitations/${code}/decline`); setState("declined"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen bg-ink relative overflow-hidden">
      <img src={POOL_NIGHT} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16">
        <img src={LOGO} alt="The D" className="h-28 sm:h-36 w-auto mix-blend-screen drop-shadow-[0_0_40px_rgba(212,175,55,0.4)]" />

        {state === "loading" && <div className="mt-10 flex items-center gap-3 text-muted-foreground"><Loader2 className="animate-spin" size={18} /> Preparing your invitation…</div>}

        {state === "error" && (
          <div className="mt-10 text-center max-w-md">
            <h1 className="font-serif text-4xl text-foreground">This invitation is unavailable</h1>
            <p className="text-muted-foreground mt-4 text-sm capitalize">{msg === "used" ? "This invitation has already been used." : msg === "expired" ? "This invitation has expired." : msg === "revoked" ? "This invitation has been revoked." : msg === "declined" ? "This invitation was declined." : "We couldn't find that invitation."}</p>
            <button onClick={() => nav("/request-invitation")} className="btn-ghost-gold px-6 py-3 text-sm mt-8">Request an Invitation</button>
          </div>
        )}

        {state === "declined" && (
          <div className="mt-10 text-center max-w-md">
            <h1 className="font-serif text-4xl">Perhaps another time.</h1>
            <p className="text-muted-foreground mt-4 text-sm">Thank you for letting us know. You're always welcome at The D.</p>
          </div>
        )}

        {state === "ok" && inv && (
          <Reveal className="mt-8 w-full max-w-xl">
            <p className="text-xs tracking-label uppercase text-pool text-center text-glow-pool">A Private Invitation</p>
            <h1 className="font-serif text-3xl sm:text-5xl text-center mt-4 leading-tight" data-testid="invite-headline">
              YOU HAVE BEEN INVITED<br />TO EXPERIENCE THE D
            </h1>
            {inv.recipient_name && <p className="text-center text-lg text-gold mt-6 font-serif italic">For {inv.recipient_name}</p>}

            <div className="glass rounded-lg p-7 mt-8 space-y-4">
              {inv.custom_message && <p className="text-sm text-foreground/90 italic border-l-2 border-gold/40 pl-4">"{inv.custom_message}"</p>}
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                {exp && <div className="flex items-center gap-3"><Gift size={16} className="text-gold" /><span>{exp.title}</span></div>}
                {(inv.valid_from || inv.valid_to) && <div className="flex items-center gap-3"><Calendar size={16} className="text-gold" /><span>{fmtDate(inv.valid_from)} – {fmtDate(inv.valid_to)}</span></div>}
                <div className="flex items-center gap-3"><Users size={16} className="text-gold" /><span>Up to {inv.guest_allowance} guests</span></div>
                {inv.expiration_date && <div className="flex items-center gap-3"><Clock size={16} className="text-gold" /><span>Respond by {fmtDate(inv.expiration_date)}</span></div>}
              </div>
              {inv.complimentary && <div className="text-center text-pool text-sm">{inv.complimentary_value > 0 ? `Complimentary — value ${money(inv.complimentary_value)}` : "Complimentary experience"}</div>}
              {inv.host_name && <p className="text-xs text-muted-foreground text-center">With warm regards, {inv.host_name}</p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button onClick={accept} data-testid="invite-accept" className="btn-gold flex-1 py-3.5 text-sm flex items-center justify-center gap-2">Accept & Reserve <ArrowRight size={16} /></button>
              <button onClick={decline} data-testid="invite-decline" className="btn-ghost-gold flex-1 py-3.5 text-sm">Respectfully Decline</button>
            </div>
            <p className="text-center font-serif italic text-gold mt-10">{SIGNATURE}</p>
          </Reveal>
        )}
      </div>
    </div>
  );
}
